import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase, TABLES } from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';
import { FiCamera } from 'react-icons/fi';
import SegmentFilter from '../../components/analysis/SegmentFilter';
import JourneyMapAnalysis from '../../components/analysis/JourneyMapAnalysis';
import ExperienceMatrixAnalysis from '../../components/analysis/ExperienceMatrixAnalysis';

const SESSION_COLORS = [
  '#3B82F6','#10B981','#F59E0B','#8B5CF6',
  '#EC4899','#06B6D4','#84CC16','#F97316',
  '#14B8A6','#6366F1','#EF4444','#A855F7',
];

const Analysis = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [showSnapshotModal, setShowSnapshotModal] = useState(false);
  const [snapName, setSnapName] = useState('');
  const [snapDesc, setSnapDesc] = useState('');
  const [savingSnap, setSavingSnap] = useState(false);
  const [activeTab, setActiveTab] = useState(null); // 'journey_map' | 'xy_plot'
  const [modules, setModules] = useState({ journey: null, matrix: null });
  const [sessions, setSessions] = useState([]);           // full enriched sessions
  const [lanes, setLanes] = useState([]);
  const [cards, setCards] = useState([]);
  const [screenerQuestions, setScreenerQuestions] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});  // { [tag_key]: [values] }

  // ── Load everything ────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Modules
      const { data: mods } = await supabase
        .from(TABLES.MODULES).select('*').eq('project_id', projectId);
      const journeyMod = mods?.find(m => m.type === 'journey_map') || null;
      const matrixMod  = mods?.find(m => m.type === 'xy_plot')     || null;
      setModules({ journey: journeyMod, matrix: matrixMod });
      if (!activeTab) setActiveTab(journeyMod ? 'journey_map' : matrixMod ? 'xy_plot' : null);

      // 2. Sessions (active + complete — don't make researchers wait)
      const { data: sessData } = await supabase
        .from(TABLES.SESSIONS).select('id, status, created_at')
        .eq('project_id', projectId)
        .in('status', ['active', 'complete'])
        .order('created_at');
      const sessionIds = sessData?.map(s => s.id) || [];
      if (sessionIds.length === 0) { setSessions([]); setLoading(false); return; }

      // 3. Segment tags
      const { data: tagData } = await supabase
        .from(TABLES.SEGMENT_TAGS).select('session_id, tag_key, tag_value')
        .in('session_id', sessionIds);

      // 4. Screener questions (for filter UI)
      const { data: qData } = await supabase
        .from(TABLES.SCREENER_QUESTIONS)
        .select('id, question_text, tag_key, options, order_index')
        .eq('project_id', projectId)
        .not('tag_key', 'is', null)
        .order('order_index');
      setScreenerQuestions(qData || []);

      // 5. Session modules
      const moduleIds = [journeyMod?.id, matrixMod?.id].filter(Boolean);
      const { data: smData } = await supabase
        .from(TABLES.SESSION_MODULES).select('id, session_id, module_id')
        .in('session_id', sessionIds)
        .in('module_id', moduleIds.length > 0 ? moduleIds : ['00000000-0000-0000-0000-000000000000']);

      // 6. Journey map placements
      const journeySmIds = smData?.filter(sm => sm.module_id === journeyMod?.id).map(sm => sm.id) || [];
      let journeyPlacements = [];
      if (journeySmIds.length > 0) {
        const { data: jpData } = await supabase
          .from(TABLES.JOURNEY_MAP_PLACEMENTS).select('*')
          .in('session_module_id', journeySmIds);
        journeyPlacements = jpData || [];
      }

      // 7. Journey map lanes
      if (journeyMod) {
        const { data: laneData } = await supabase
          .from(TABLES.JOURNEY_MAP_LANES).select('*').eq('module_id', journeyMod.id);
        setLanes(laneData || []);
      }

      // 8. Matrix placements
      const matrixSmIds = smData?.filter(sm => sm.module_id === matrixMod?.id).map(sm => sm.id) || [];
      let matrixPlacements = [];
      if (matrixSmIds.length > 0) {
        const { data: mpData } = await supabase
          .from(TABLES.XY_PLOT_PLACEMENTS).select('*')
          .in('session_module_id', matrixSmIds);
        matrixPlacements = mpData || [];
      }

      // 9. Touchpoint cards
      if (matrixMod) {
        const { data: cardData } = await supabase
          .from(TABLES.TOUCHPOINT_CARDS).select('*').eq('module_id', matrixMod.id).order('order_index');
        setCards(cardData || []);
      }

      // ── Assemble sessions ────────────────────────────────────────────────
      const tagsBySession = {};
      tagData?.forEach(t => {
        if (!tagsBySession[t.session_id]) tagsBySession[t.session_id] = [];
        tagsBySession[t.session_id].push(t);
      });

      // A session+module can have MORE THAN ONE session_modules row (legacy
      // duplicates from an old race). Collect ALL ids so we don't lose placements
      // written under a different duplicate than the last one seen.
      const smBySessionModule = {};
      smData?.forEach(sm => {
        if (!smBySessionModule[sm.session_id]) smBySessionModule[sm.session_id] = {};
        if (!smBySessionModule[sm.session_id][sm.module_id]) smBySessionModule[sm.session_id][sm.module_id] = [];
        smBySessionModule[sm.session_id][sm.module_id].push(sm.id);
      });

      const jpBySmId = {};
      journeyPlacements.forEach(p => {
        if (!jpBySmId[p.session_module_id]) jpBySmId[p.session_module_id] = [];
        jpBySmId[p.session_module_id].push(p);
      });

      const mpBySmId = {};
      matrixPlacements.forEach(p => {
        if (!mpBySmId[p.session_module_id]) mpBySmId[p.session_module_id] = [];
        mpBySmId[p.session_module_id].push(p);
      });

      const enriched = (sessData || []).map((session, idx) => {
        const journeySmIds = journeyMod ? (smBySessionModule[session.id]?.[journeyMod.id] || []) : [];
        const matrixSmIds  = matrixMod  ? (smBySessionModule[session.id]?.[matrixMod.id]  || []) : [];

        const milestones = journeySmIds.flatMap(smId => jpBySmId[smId] || [])
          .map(p => ({
            id: p.id,
            x_position: p.x_position,
            y_position: p.y_position,
            label: p.metadata?.label || '',
            time_from_previous: p.metadata?.time_from_previous || null,
            order_index: p.metadata?.order_index ?? 0,
            researcher_tag: p.researcher_tag || null,
          }))
          .sort((a, b) => a.x_position - b.x_position);

        const matrixPlacements2 = matrixSmIds.flatMap(smId => mpBySmId[smId] || [])
          .map(p => ({
            id: p.id,
            x_value: p.x_value,
            y_value: p.y_value,
            touchpoint_card_id: p.touchpoint_card_id || null,
            custom_label: p.custom_label || null,
            label: p.custom_label || null,
            is_custom: p.is_custom || false,
          }));

        return {
          id: session.id,
          status: session.status,
          // Stable project-level participant number (created-at order across ALL
          // sessions) — stays consistent through filtering and into snapshots, so
          // segmentation data keyed by participant number never gets renumbered.
          participantNumber: idx + 1,
          color: SESSION_COLORS[idx % SESSION_COLORS.length],
          tags: tagsBySession[session.id] || [],
          milestones,
          matrixPlacements: matrixPlacements2,
        };
      });

      setSessions(enriched);
    } catch (err) {
      console.error('Analysis load error:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Filter logic ──────────────────────────────────────────────────────────
  const filteredSessions = sessions.filter(session => {
    return Object.entries(activeFilters).every(([key, selected]) => {
      if (selected.length === 0) return true; // no filter for this dimension
      return session.tags.some(t => t.tag_key === key && selected.includes(t.tag_value));
    });
  });

  const handleFilterChange = (key, values) => {
    setActiveFilters(prev => ({ ...prev, [key]: values }));
  };

  // ── Save current journey view as a snapshot ───────────────────────────────
  const createSnapshot = async () => {
    if (!snapName.trim() || !modules.journey) return;
    setSavingSnap(true);
    const journeyCfg = modules.journey.config || {};
    // Freeze the currently-filtered journey data (independent of the live module)
    const frozen = {
      sessions: filteredSessions.map(s => ({
        id: s.id, participantNumber: s.participantNumber, color: s.color, status: s.status, tags: s.tags, milestones: s.milestones,
      })),
      lanes,
    };
    const filter_config = filterDimensions.map(d => ({
      key: d.key,
      label: d.label,
      values: d.values,
      selected: activeFilters[d.key] || [],
      locked: true, // Phase 3 will let researchers mark some dimensions interactive
    }));
    const { data, error } = await supabase.from(TABLES.JOURNEY_SNAPSHOTS).insert([{
      project_id: projectId,
      source_module_id: modules.journey.id,
      name: snapName.trim(),
      description: snapDesc.trim() || null,
      view_mode: 'combined',
      h_stretch: journeyCfg.h_stretch ?? 1.4,
      tag_positions: journeyCfg.tag_positions || {},
      filter_config,
      data: frozen,
    }]).select('id').single();
    setSavingSnap(false);
    if (error) { console.error('snapshot save failed:', error); alert('Could not save snapshot.'); return; }
    setShowSnapshotModal(false);
    setSnapName(''); setSnapDesc('');
    navigate(`/projects/${projectId}/snapshots/${data.id}`);
  };

  // ── Filter dimensions from screener questions ─────────────────────────────
  const filterDimensions = screenerQuestions
    .filter(q => q.tag_key)
    .map(q => {
      // Find options that actually appear in sessions
      const observedValues = [...new Set(
        sessions.flatMap(s => s.tags.filter(t => t.tag_key === q.tag_key).map(t => t.tag_value))
      )];
      // Map values to labels using the original question options
      const optionMap = Object.fromEntries((q.options || []).map(o => [o.value, o.label]));
      return {
        key: q.tag_key,
        label: q.question_text,
        values: observedValues.map(v => ({ value: v, label: optionMap[v] || v })),
      };
    })
    .filter(d => d.values.length > 0);

  const tabs = [
    modules.journey && { id: 'journey_map', label: 'Journey Map' },
    modules.matrix  && { id: 'xy_plot',     label: 'Touchpoint Mapping' },
  ].filter(Boolean);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rutabaga-pink" />
      </div>
    );
  }

  if (tabs.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <p className="font-black text-rutabaga-dark text-lg">No modules configured</p>
          <p className="text-gray-400 text-sm mt-1">Add a journey map or touchpoint mapping module to this project first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">

      {/* ── Segment filter sidebar ───────────────────────────────────────── */}
      <SegmentFilter
        dimensions={filterDimensions}
        activeFilters={activeFilters}
        onChange={handleFilterChange}
        sessionCount={filteredSessions.length}
      />

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Module tabs */}
        <div className="bg-white border-b border-gray-200 flex items-center px-5 gap-1 shrink-0">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-[10px] font-black uppercase tracking-widest border-b-2 transition-colors
                ${activeTab === tab.id
                  ? 'border-rutabaga-pink text-rutabaga-pink'
                  : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            >
              {tab.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-3 py-2">
            {Object.values(activeFilters).flat().length > 0 && (
              <span className="text-[9px] font-black text-rutabaga-pink uppercase tracking-widest bg-rutabaga-pink/10 px-2 py-1 rounded-full">
                {Object.values(activeFilters).flat().length} filter{Object.values(activeFilters).flat().length !== 1 ? 's' : ''} active
              </span>
            )}
            <span className="text-[9px] font-black text-gray-300 uppercase tracking-widest">
              {filteredSessions.length} of {sessions.length} session{sessions.length !== 1 ? 's' : ''}
            </span>
            {activeTab === 'journey_map' && filteredSessions.length > 0 && (
              <button
                onClick={() => setShowSnapshotModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-rutabaga-dark text-white text-[9px] font-black uppercase tracking-widest hover:bg-black transition-colors"
              >
                <SafeIcon icon={FiCamera} /> Save as snapshot
              </button>
            )}
          </div>
        </div>

        {/* Analysis canvas */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'journey_map' && modules.journey && (
            <JourneyMapAnalysis
              sessions={filteredSessions}
              lanes={lanes}
              module={modules.journey}
              onSessionsUpdate={setSessions}
            />
          )}
          {activeTab === 'xy_plot' && modules.matrix && (
            <ExperienceMatrixAnalysis
              sessions={filteredSessions}
              cards={cards}
              config={modules.matrix?.config || {}}
            />
          )}
        </div>
      </div>

      {/* ── Save-as-snapshot modal ───────────────────────────────────────── */}
      {showSnapshotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !savingSnap && setShowSnapshotModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-black text-rutabaga-dark tracking-tight">Save as snapshot</h2>
            <p className="text-xs text-gray-400 mt-1">
              Freezes the current view — {filteredSessions.length} session{filteredSessions.length !== 1 ? 's' : ''},
              {Object.values(activeFilters).flat().length > 0
                ? ` ${Object.values(activeFilters).flat().length} active filter${Object.values(activeFilters).flat().length !== 1 ? 's' : ''},`
                : ''} current spacing &amp; tag positions. Editing a snapshot won&apos;t affect the original.
            </p>

            <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mt-4 mb-1">Name</label>
            <input
              autoFocus
              value={snapName}
              onChange={e => setSnapName(e.target.value)}
              placeholder="e.g. ESL customers — registration friction"
              className="w-full text-sm border border-gray-200 rounded px-3 py-2 outline-none focus:border-rutabaga-pink"
            />

            <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mt-3 mb-1">Description</label>
            <textarea
              value={snapDesc}
              onChange={e => setSnapDesc(e.target.value)}
              rows={3}
              placeholder="What story does this snapshot tell?"
              className="w-full text-sm border border-gray-200 rounded px-3 py-2 outline-none focus:border-rutabaga-pink resize-none"
            />

            {Object.values(activeFilters).flat().length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {filterDimensions.flatMap(d =>
                  (activeFilters[d.key] || []).map(v => {
                    const opt = d.values.find(o => o.value === v);
                    return (
                      <span key={`${d.key}-${v}`} className="text-[9px] font-black uppercase tracking-wide bg-rutabaga-pink/10 text-rutabaga-pink px-2 py-0.5 rounded-full">
                        {d.label}: {opt?.label || v}
                      </span>
                    );
                  })
                )}
              </div>
            )}

            <div className="flex gap-2 mt-6">
              <button
                onClick={createSnapshot}
                disabled={!snapName.trim() || savingSnap}
                className="flex-1 bg-rutabaga-pink text-white text-[10px] font-black uppercase tracking-widest py-2.5 rounded hover:bg-rutabaga-pink-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {savingSnap ? 'Saving…' : 'Create snapshot'}
              </button>
              <button
                onClick={() => setShowSnapshotModal(false)}
                disabled={savingSnap}
                className="px-4 text-[10px] font-black text-gray-400 hover:text-gray-600 uppercase tracking-widest"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analysis;
